import type { IOption } from "models/Form";
import type { IPaymentProfile } from "models/HaulPay";

export type PreviousMetadata = {
  previousPaymentProfileId: string;
  previousPaymentProfileAmount: number;
  previousDiscountRate: number;
  previousFactoringFeeSplitRate: number;
  previousPayoutDays: number;
};

export type NonFactoredToFactoredResult = {
  carrier: Partial<IPaymentProfile>;
  carrierInvoiceAmount: number;
  carrierPaymentSpeed: IOption;
  brokersQRFee: IOption;
};

const NON_FACTORED_TO_FACTORED_MODAL_ID = "non-factored-to-factored";

const createAbortError = () => {
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";

  return error;
};

interface ConfirmNonFactoredToFactoredArgs {
  openModal: (modalId: string, props: Record<string, unknown>) => void;
  closeModal?: (modalId: string) => void;
  fromContractType?: string;
  toContractType?: string;
  previousMetadata?: PreviousMetadata;
  signal?: AbortSignal;
}

export const confirmNonFactoredToFactored = ({
  openModal,
  closeModal,
  fromContractType,
  toContractType,
  previousMetadata,
  signal
}: ConfirmNonFactoredToFactoredArgs) =>
  new Promise<NonFactoredToFactoredResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    let isSettled = false;

    const cleanupAbortListener = () => {
      if (signal && handleAbort) {
        signal.removeEventListener("abort", handleAbort);
      }
    };

    const settleResolve = (value: NonFactoredToFactoredResult) => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      resolve(value);
    };

    const settleReject = (reason: unknown) => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      reject(reason);
    };

    const handleAbort = () => {
      closeModal?.(NON_FACTORED_TO_FACTORED_MODAL_ID);
      settleReject(createAbortError());
    };

    signal?.addEventListener("abort", handleAbort, { once: true });

    openModal(NON_FACTORED_TO_FACTORED_MODAL_ID, {
      fromContractType,
      toContractType,
      previousMetadata,
      onConfirm: (result: NonFactoredToFactoredResult) => {
        if (isSettled || signal?.aborted) {
          return;
        }

        settleResolve(result);
      },
      onCloseModal: () => {
        if (isSettled || signal?.aborted) {
          return;
        }

        settleReject("cancel-edit-fr");
      }
    });
  });
